// Passkeys in the app (auth/passkeys.h on the server): logging in with
// the phone's fingerprint or Windows Hello instead of typing the password.
//
// The browser does the cryptography (navigator.credentials); this file only
// turns the server's options into WebAuthn options and the result into
// base64url fields. Keys are discoverable, so the login screen needs no
// login name: the phone asks which family member's passkey to use.
import { ApiError, api, errorText } from '../../api/client'
import type { PasskeyCreateOptions, PasskeyLoginOptions } from '../../api/types'
import { b64urlToBytes } from '../push/push'

const ES256 = -7

export function bytesToB64url(buf: ArrayBuffer | ArrayBufferView): string {
  const b = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)
  let bin = ''
  for (const x of b) bin += String.fromCharCode(x)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The browser can make and use passkeys here (secure context, WebAuthn). */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && typeof PublicKeyCredential === 'function'
}

/** The user closed the fingerprint prompt or it timed out: not an error to show. */
export function isCancel(e: unknown): boolean {
  return e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError')
}

export function creationOptions(o: PasskeyCreateOptions): CredentialCreationOptions {
  return {
    publicKey: {
      challenge: b64urlToBytes(o.challenge),
      rp: { id: o.rp_id, name: o.rp_name },
      user: { id: b64urlToBytes(o.user_id), name: o.user_name, displayName: o.user_display_name },
      pubKeyCredParams: [{ type: 'public-key', alg: ES256 }],
      excludeCredentials: o.exclude.map((id) => ({ type: 'public-key' as const, id: b64urlToBytes(id) })),
      authenticatorSelection: { residentKey: 'required', requireResidentKey: true, userVerification: 'required' },
      attestation: 'none',
      timeout: o.timeout_ms,
    },
  }
}

export function requestOptions(o: PasskeyLoginOptions): CredentialRequestOptions {
  return {
    publicKey: {
      challenge: b64urlToBytes(o.challenge),
      rpId: o.rp_id,
      allowCredentials: [],
      userVerification: 'required',
      timeout: o.timeout_ms,
    },
  }
}

/** Makes a passkey on this device and registers it under name. */
export async function addPasskey(name: string): Promise<void> {
  const options = await api.passkeyOptions()
  const cred = (await navigator.credentials.create(creationOptions(options))) as PublicKeyCredential | null
  if (!cred) throw new DOMException('no credential', 'NotAllowedError')
  const res = cred.response as AuthenticatorAttestationResponse
  await api.addPasskey({
    client_data: bytesToB64url(res.clientDataJSON),
    attestation: bytesToB64url(res.attestationObject),
    name,
  })
}

/** Signs in with a passkey the person picks; the session cookie is set. */
export async function loginWithPasskey(device_name: string): Promise<void> {
  const options = await api.passkeyLoginOptions()
  const cred = (await navigator.credentials.get(requestOptions(options))) as PublicKeyCredential | null
  if (!cred) throw new DOMException('no credential', 'NotAllowedError')
  const res = cred.response as AuthenticatorAssertionResponse
  await api.passkeyLogin({
    credential_id: bytesToB64url(cred.rawId),
    client_data: bytesToB64url(res.clientDataJSON),
    auth_data: bytesToB64url(res.authenticatorData),
    signature: bytesToB64url(res.signature),
    ...(res.userHandle ? { user_handle: bytesToB64url(res.userHandle) } : {}),
    device_name,
  })
}

/** What to show for a failed passkey step; null when the person cancelled. */
export function passkeyErrorText(e: unknown): string | null {
  if (isCancel(e)) return null
  if (e instanceof DOMException) {
    switch (e.name) {
      case 'InvalidStateError':
        return '이 기기에는 이미 등록한 패스키가 있습니다.'
      case 'NotSupportedError':
        return '이 기기에서는 패스키를 만들 수 없습니다.'
      case 'SecurityError':
        return '이 주소에서는 패스키를 쓸 수 없습니다.'
      default:
        return '패스키를 쓰지 못했습니다.'
    }
  }
  if (e instanceof ApiError) {
    if (e.code === 'invalid_credentials')
      return '패스키를 확인하지 못했습니다. 지운 패스키일 수 있으니 비밀번호로 로그인해 주세요.'
    if (e.code === 'not_found' && e.message.startsWith('passkeys')) return '이 서버에서는 아직 패스키를 쓸 수 없습니다.'
    if (e.code === 'conflict')
      return e.message.startsWith('too many') ? '패스키는 10개까지 등록할 수 있습니다.' : '이미 등록한 패스키입니다.'
  }
  return errorText(e)
}
